import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldHelperProps, FieldInputProps } from 'formik';
import type { ComponentType, FC, ReactElement } from 'react';
import type { DatePickerProps as AntDatePickerProps } from 'antd';
import { AStyledDatePicker } from 'components/ui/composed/declarations/formDatepicker/DeclarationDatepicker.styles';
import { parseToMoment } from 'core/utils/date';

export type DatePickerInputValue = string | Date | moment.Moment | null | undefined;

const DEFAULT_FORMATS = {
    acceptedFormats: ['YYYY-MM-DD', 'YYYYMMDD', 'YYYY-MM-DD HH:mm', 'YYYYMMDDHHmm'],
    dateOnlyDisplay: ['YYYY-MM-DD', 'YYYYMMDD', 'YYYY-MM-DD HH:mm', 'YYYYMMDDHHmm'],
    dateTimeDisplay: ['YYYY-MM-DD HH:mm', 'YYYYMMDDHHmm', 'YYYY-MM-DD', 'YYYYMMDD'],
};

export type DatePickerFormats = {
    acceptedFormats?: string[];
    dateOnlyDisplay?: string[];
    dateTimeDisplay?: string[];
};

interface DatePickerProps {
    showTime?: boolean;
    style?: React.CSSProperties;
    id?: string;
    condensed?: boolean;
    disabled?: boolean;
    placeholder?: string;
    status?: '' | 'warning' | 'error';
    lock?: boolean;
    fieldProps: FieldInputProps<any>;
    fieldHelpers?: FieldHelperProps<any>;
    onFocus?: React.FocusEventHandler<HTMLElement>;
    onBlur?: React.FocusEventHandler<HTMLElement>;
    formats?: DatePickerFormats;
    onChange?: (value: moment.Moment | null) => void;
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    serializeDate?: (value: DatePickerInputValue) => string;
    DatePickerComponent?: ComponentType<AntDatePickerProps & any>;
}

const defaultGetPopupContainer = (trigger: HTMLElement) => trigger.parentElement ?? trigger;

const firstNonEmpty = (...vals: Array<string | undefined | null>) =>
    vals.map((v) => (v ?? '').trim()).find((v) => v.length > 0) ?? '';

const DDatePicker: FC<DatePickerProps> = ({
    lock,
    fieldProps,
    fieldHelpers,
    showTime = false,
    onFocus,
    onBlur,
    onChange,
    id,
    condensed,
    disabled,
    placeholder,
    status,
    getPopupContainer,
    formats,
    serializeDate,
    DatePickerComponent,
    style,
}): ReactElement => {
    const acceptedFormats = formats?.acceptedFormats ?? DEFAULT_FORMATS.acceptedFormats;
    const dateOnlyDisplay = formats?.dateOnlyDisplay ?? DEFAULT_FORMATS.dateOnlyDisplay;
    const dateTimeDisplay = formats?.dateTimeDisplay ?? DEFAULT_FORMATS.dateTimeDisplay;

    const [open, setOpen] = useState(false);

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // The current value in the DOM input
    const inputRef = useRef<HTMLInputElement | null>(null);

    // The value the user actually typed
    const typedTextRef = useRef<string>('');

    // Clicking Today / Now commits a new value, but then closing
    // the picker immediately commits the old value again, overriding it
    // This is needed in order to set the date by clicking Today / Now
    // (It was not working on some places like Ireland H1 import)
    const skipNextDraftCommitRef = useRef(false);

    const value = useMemo(
        () => parseToMoment(fieldProps.value, acceptedFormats, true),
        [fieldProps.value, acceptedFormats]
    );

    const toFormikString = useCallback(
        (m: moment.Moment | null): string => {
            if (!m) return '';
            if (serializeDate) return serializeDate(m);
            return m.format(showTime ? 'YYYY-MM-DDTHH:mm:ssZ' : 'YYYY-MM-DD');
        },
        [serializeDate, showTime]
    );

    const commitMoment = useCallback(
        (m: moment.Moment | null) => {
            if (onChange) return onChange(m);
            fieldHelpers?.setValue(toFormikString(m));
        },
        [fieldHelpers, onChange, toFormikString]
    );

    const commitDraftIfValid = useCallback(() => {
        if (skipNextDraftCommitRef.current) {
            skipNextDraftCommitRef.current = false;
            typedTextRef.current = '';
            return;
        }

        const text = firstNonEmpty(typedTextRef.current, inputRef.current?.value);
        if (!text) return;

        const m = parseToMoment(text, acceptedFormats, true);
        if (m && m.isValid()) {
            commitMoment(m);
        }

        typedTextRef.current = '';
    }, [acceptedFormats, commitMoment]);

    // Track draft typing via native input event
    useEffect(() => {
        const input = wrapperRef.current?.querySelector('input') ?? null;
        inputRef.current = input;
        if (!input) return;

        const onInput = () => {
            typedTextRef.current = input.value;
        };

        input.addEventListener('input', onInput);
        return () => input.removeEventListener('input', onInput);
    }, []);

    // Set the date on "Now"/"Today" button click
    useEffect(() => {
        if (!open) return;

        const onPointerDownCapture = (e: PointerEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;

            const isFooterAction = !!target.closest('.ant-picker-now-btn') || !!target.closest('.ant-picker-today-btn');

            if (isFooterAction) skipNextDraftCommitRef.current = true;
        };

        // pointer down fires before on blur
        document.addEventListener('pointerdown', onPointerDownCapture, true);
        return () => document.removeEventListener('pointerdown', onPointerDownCapture, true);
    }, [open]);

    const Picker = DatePickerComponent ?? AStyledDatePicker;

    return (
        <div ref={wrapperRef} style={style}>
            <Picker
                id={id}
                style={style}
                name={fieldProps.name}
                disabled={disabled}
                placeholder={placeholder}
                lock={lock}
                allowClear
                value={value}
                format={showTime ? dateTimeDisplay : dateOnlyDisplay}
                showTime={showTime ? { format: 'HH:mm', showNow: true } : undefined}
                onChange={commitMoment}
                onOpenChange={(isOpen) => {
                    if (open && !isOpen) {
                        commitDraftIfValid();
                    }
                    setOpen(isOpen);
                }}
                onBlur={(e) => {
                    fieldHelpers?.setTouched(true);
                    onBlur?.(e);
                }}
                onFocus={onFocus}
                condensed={condensed}
                status={status}
                getPopupContainer={getPopupContainer ?? defaultGetPopupContainer}
            />
        </div>
    );
};

export default DDatePicker;
